// One item on the menu page: the name (two lines at most), a muted line with its prices and its wine's producer, then
// compact allergen badges with text (ACC-04). A hidden item reads dimmed with a "Nascosto" badge, so it can still be
// found and switched back. The row opens the item's drawer.
import type { KeyboardEvent } from "react";
import { cn } from "@/utils/cn";
import { allergenName } from "../allergens";
import { pricesLine, wineLine } from "../format";
import type { AllergenLevel, MenuItem } from "../types";

interface Props {
  item: MenuItem;
  /** The category's name, shown above the name in search results. */
  context?: string;
  onOpen: () => void;
}

export function HiddenBadge() {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-gray-200 px-[7px] py-[1px] text-[11px] leading-[16px] font-medium text-gray-600">
      Nascosto
    </span>
  );
}

function AllergenBadge({ name, level }: { name: string; level: AllergenLevel }) {
  const may = level === "may_contain";
  return (
    <span
      title={may ? `Può contenere: ${name}` : `Contiene: ${name}`}
      aria-label={may ? `Può contenere ${name}` : `Contiene ${name}`}
      className={cn(
        "inline-flex items-center rounded-full px-[7px] py-[1px] text-[11px] leading-[16px] font-medium",
        may ? "border border-gray-300 text-gray-500" : "border border-transparent bg-gray-100 text-gray-700",
      )}
    >
      {name}
    </span>
  );
}

export function ItemRow({ item, context, onOpen }: Props) {
  const tail = [pricesLine(item), wineLine(item)].filter(Boolean).join(" · ");
  // Contiene before Può contenere, each in the order the item lists them.
  const allergens = (Object.entries(item.allergens) as [string, AllergenLevel][])
    .sort(([, a], [, b]) => (a === b ? 0 : a === "contains" ? -1 : 1));
  const open = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={open}
      data-item-id={item.id}
      className="-mx-1 flex cursor-pointer items-center gap-1 rounded-lg py-2.5 pl-1 pr-1 transition-colors hover:bg-gray-50/80"
    >
      <div className={cn("min-w-0 flex-1", item.hidden && "opacity-60")}>
        {context && <p className="truncate text-[11px] leading-4 text-gray-400">{context}</p>}
        <p className="line-clamp-2 text-[13px] font-medium leading-[18px] text-gray-900 [overflow-wrap:anywhere]">{item.name}</p>
        {tail && <p className="mt-0.5 truncate text-[12px] leading-[17px] text-gray-500">{tail}</p>}
        {(allergens.length > 0 || item.hidden) && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {item.hidden && <HiddenBadge />}
            {allergens.map(([id, level]) => (
              <AllergenBadge key={id} name={allergenName(id)} level={level} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
